const config = window.Diecela || {};

export const debounce = (fn, wait = 250) => {
  let t;
  let pending = null;
  const run = (...args) => {
    pending = args;
    clearTimeout(t);
    t = setTimeout(() => {
      pending = null;
      fn(...args);
    }, wait);
  };
  run.cancel = () => {
    clearTimeout(t);
    pending = null;
  };

  run.flush = () => {
    if (!pending) return;
    clearTimeout(t);
    const args = pending;
    pending = null;
    fn(...args);
  };
  return run;
};

export const parseHTML = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('noscript').forEach((el) => el.remove());
  return doc;
};

export async function fetchSection(url, sectionId, { signal } = {}) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u, { signal });
  if (!res.ok) throw new Error(`Section request failed (${res.status})`);
  return parseHTML(await res.text());
}

export async function fetchSections(ids, url = window.location.pathname) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('sections', ids.join(','));
  const res = await fetch(u);
  const json = await res.json();
  const out = {};
  for (const id of ids) if (json[id]) out[id] = parseHTML(json[id]);
  return out;
}

export function swapContent(target, source) {
  if (!target || !source) return;
  const active = document.activeElement;
  const activeId = active && target.contains(active) ? active.id : null;
  target.replaceChildren(...Array.from(source.childNodes).map((n) => document.importNode(n, true)));
  if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
}

export function formatMoney(cents, format = config.moneyFormat || '${{amount}}') {
  const value = Number(cents) || 0;
  const place = (n, decimals, thousands = ',', decimal = '.') => {
    const fixed = (n / 100).toFixed(decimals);
    const [int, frac] = fixed.split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount': return place(value, 2);
      case 'amount_no_decimals': return place(value, 0);
      case 'amount_with_comma_separator': return place(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return place(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return place(value, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator': return place(value, 0, ' ');
      case 'amount_with_space_separator': return place(value, 2, ' ', ',');
      case 'amount_with_period_and_space_separator': return place(value, 2, ' ', '.');
      default: return place(value, 2);
    }
  });
}

export const events = {
  on(name, fn) {
    document.addEventListener(name, fn);
    return () => document.removeEventListener(name, fn);
  },
  emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  },
};

export function announce(message, { toast = false } = {}) {
  const region = document.getElementById('a11y-live');
  if (region) {
    region.textContent = '';
    setTimeout(() => (region.textContent = message), 60);
  }
  if (!toast) return;
  let el = document.querySelector('.toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('aria-hidden', 'true');
    document.body.append(el);
  }
  el.textContent = message;
  el.toggleAttribute('data-visible', true);
  clearTimeout(el._t);
  el._t = setTimeout(() => el.removeAttribute('data-visible'), 3200);
}

let swiperLoader = null;

export function ensureSwiper() {
  if (window.Swiper) return Promise.resolve(window.Swiper);
  if (swiperLoader) return swiperLoader;
  const js = config.swiperJs;
  if (!js) return Promise.resolve(null);
  swiperLoader = new Promise((resolve) => {
    if (config.swiperCss && !document.querySelector('link[data-swiper-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = config.swiperCss;
      link.dataset.swiperCss = '';
      const base = document.querySelector('link[rel="stylesheet"][href*="base.css"]');
      if (base) base.before(link);
      else document.head.append(link);
    }
    const script = document.createElement('script');
    script.src = js;
    script.async = true;
    script.addEventListener('load', () => resolve(window.Swiper || null));
    script.addEventListener('error', () => resolve(null));
    document.head.append(script);
  });
  return swiperLoader;
}

let aosLoader = null;

function ensureAOS() {
  if (window.AOS) return Promise.resolve(window.AOS);
  if (aosLoader) return aosLoader;
  const js = config.aosJs;
  if (!js) return Promise.resolve(null);
  aosLoader = new Promise((resolve) => {
    if (config.aosCss && !document.querySelector('link[data-aos-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = config.aosCss;
      link.dataset.aosCss = '';
      document.head.append(link);
    }
    const script = document.createElement('script');
    script.src = js;
    script.async = true;
    script.addEventListener('load', () => resolve(window.AOS || null));
    script.addEventListener('error', () => resolve(null));
    document.head.append(script);
  });
  return aosLoader;
}

const REVEAL_TARGETS = '.reveal > .page-width, .reveal > .reveal-target';

function markRevealTargets(root = document, effect = 'fade-up') {
  const targets = root.querySelectorAll(REVEAL_TARGETS);
  targets.forEach((el) => {
    if (el.closest('.shopify-section-group-footer-group')) return;
    if (el.querySelector('[data-animate], [data-aos]')) return;
    el.dataset.aos = effect;
  });
  return targets.length;
}

function applyAnimations(root = document) {
  root.querySelectorAll('[data-animate]').forEach((el) => {
    el.dataset.aos = el.dataset.animate;
    delete el.dataset.animate;
  });
}

async function setupReveal() {
  if (prefersReducedMotion() || document.body.classList.contains('shopify-design-mode')) return;

  const site = config.aosSite === true;
  const authored = document.querySelector('[data-animate]');
  const fallback = !site && !CSS.supports('animation-timeline', 'view()') && document.querySelector(REVEAL_TARGETS);
  if (!site && !authored && !fallback) return;

  const AOS = await ensureAOS();
  if (!AOS) return;
  const effect = site ? config.aosEffect || 'fade-up' : 'fade-up';
  applyAnimations();
  if (site || fallback) markRevealTargets(document, effect);
  AOS.init({
    duration: Number(config.aosDuration) || 500,
    easing: 'ease-out',
    offset: 60,
    once: config.aosOnce !== false,
  });

  document.addEventListener('shopify:section:load', (e) => {
    applyAnimations(e.target);
    if (site || fallback) markRevealTargets(e.target, effect);
    AOS.refreshHard();
  });
}
export const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
export const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

setupReveal();

export function focusQuietly(el) {
  if (!el) return;
  const x = window.scrollX;
  const y = window.scrollY;
  el.focus({ preventScroll: true });

  if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y);
}

export function openDialog(dialog, opener) {
  if (!dialog || dialog.open) return;
  dialog._opener = opener || document.activeElement;
  dialog.showModal();
  if (dialog.classList.contains('search-modal')) placeSearchPopover(dialog, opener);

  dialog.setAttribute('data-opening', '');
  clearTimeout(dialog._openTimer);
  dialog._openTimer = setTimeout(() => dialog.removeAttribute('data-opening'), 900);
  dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  opener?.setAttribute('aria-expanded', 'true');
}

// Desktop/tablet: the search opens as a compact panel right under the icon that opened it.
function placeSearchPopover(dialog, opener) {
  if (!opener || !window.matchMedia('(min-width: 750px)').matches) return;
  const r = opener.getBoundingClientRect();
  const right = Math.max(16, window.innerWidth - r.right - 8);
  dialog.style.setProperty('--sp-top', `${Math.round(r.bottom + 10)}px`);
  dialog.style.setProperty('--sp-right', `${Math.round(right)}px`);
  dialog.style.setProperty('--sp-arrow', `${Math.round(window.innerWidth - right - (r.left + r.width / 2))}px`);
}

export function closeDialog(dialog) {
  if (!dialog?.open) return;
  clearTimeout(dialog._openTimer);
  dialog.removeAttribute('data-opening');
  dialog.close();
}

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    if (dialog) {
      e.preventDefault();
      openDialog(dialog, opener);
    }
    return;
  }
  const closer = e.target.closest('[data-dialog-close]');
  if (closer) {
    closeDialog(closer.closest('dialog'));
    return;
  }

  if (e.target instanceof HTMLDialogElement && e.target.open) {
    const r = e.target.getBoundingClientRect();
    const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside || e.target.dataset.lightDismiss === 'always') closeDialog(e.target);
  }
});

document.addEventListener(
  'close',
  (e) => {
    const dialog = e.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    const opener = dialog._opener;
    opener?.setAttribute?.('aria-expanded', 'false');
    if (opener?.isConnected) opener.focus({ preventScroll: true });
    dialog.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
  },
  true
);

const SMOOTH_DETAILS = [
  '.accordion__row',
  '.footer-menu--accordion',
  '.gift-recipient',
  '.facets-sidebar .facet',
  '.drawer .facet',
  'cart-discount > details',
  'cart-note > details',
].join(', ');

function slideDetails(details, content) {
  const closing = details.open;
  const running = details._detailsAnim;
  const start = running ? content.getBoundingClientRect().height : closing ? content.scrollHeight : 0;
  running?.cancel();
  if (!closing) details.open = true;
  const target = closing ? 0 : content.scrollHeight;
  const duration = Math.round(Math.min(320, Math.max(140, 140 + Math.abs(target - start) * 0.35)));

  content.style.overflow = 'clip';
  content.style.willChange = 'height';
  const anim = content.animate(
    {
      height: [`${start}px`, `${target}px`],
      opacity: closing ? [1, 0.2] : [0.45, 1],
    },
    { duration, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' }
  );
  const cleanup = () => {
    content.style.overflow = '';
    content.style.willChange = '';
  };
  details._detailsAnim = anim;
  anim.addEventListener('cancel', () => {
    if (details._detailsAnim === anim) details._detailsAnim = null;
  });
  anim.addEventListener('finish', () => {
    if (details._detailsAnim !== anim) return;
    details._detailsAnim = null;
    cleanup();
    if (closing) details.open = false;
  });
}

document.addEventListener('click', (e) => {
  const summary = e.target.closest?.('summary');
  if (!summary || e.target.closest('a, button, input, select, textarea')) return;
  const details = summary.parentElement;
  if (!(details instanceof HTMLDetailsElement) || !details.matches(SMOOTH_DETAILS)) return;
  if (prefersReducedMotion()) return;
  const content = summary.nextElementSibling;
  if (!content) return;
  e.preventDefault();
  slideDetails(details, content);
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const open = e.target.closest?.('details[open][data-auto-close]');
  if (open) {
    e.preventDefault();
    open.removeAttribute('open');
    open.querySelector('summary')?.focus();
  }
});

document.addEventListener('click', (e) => {
  document.querySelectorAll('details[open][data-auto-close]').forEach((d) => {
    if (!d.contains(e.target)) d.removeAttribute('open');
  });
});

document.addEventListener('focusout', (e) => {
  const d = e.target.closest?.('details[open][data-auto-close]');
  if (d && e.relatedTarget && !d.contains(e.relatedTarget)) d.removeAttribute('open');
});

document.addEventListener(
  'toggle',
  (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    const group = d.parentElement?.closest('[data-exclusive]');
    if (!group) return;
    group.querySelectorAll(':scope details[open]').forEach((other) => {
      if (other === d || other.contains(d) || d.contains(other)) return;
      const content = other.querySelector(':scope > summary')?.nextElementSibling;
      if (content && !prefersReducedMotion() && other.matches(SMOOTH_DETAILS)) {
        slideDetails(other, content);
        return;
      }
      other.removeAttribute('open');
    });
  },
  true
);

class DeferredMedia extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector(':scope > button');
    this.button?.addEventListener('click', () => this.load(true), { once: true });
    if (this.hasAttribute('autoplay')) {
      this.io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          this.load(false);
          this.io.disconnect();
        }
      }, { rootMargin: '200px' });
      this.io.observe(this);
    }
  }
  disconnectedCallback() {
    this.io?.disconnect();
  }
  load(focus) {
    if (this.hasAttribute('loaded')) return;
    const template = this.querySelector('template');
    if (!template) return;
    const content = template.content.firstElementChild.cloneNode(true);
    this.setAttribute('loaded', '');
    this.append(content);
    if (content.tagName === 'VIDEO') {
      content.play?.().catch(() => {});
    }
    if (this.hasAttribute('data-model') && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: (errors) => {
        if (errors) return;
        const viewer = this.querySelector('model-viewer');
        if (viewer && window.Shopify.ModelViewerUI) new window.Shopify.ModelViewerUI(viewer);
      } }]);
    }
    if (focus) content.focus?.();
  }
}
customElements.define('deferred-media', DeferredMedia);

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', this.onClick);
    this.input.addEventListener('change', () => this.sync());
    this.sync();
  }
  onClick = (e) => {
    const btn = e.target.closest('button[name]');
    if (!btn) return;
    e.preventDefault();
    const step = Number(this.input.step) || 1;
    const prev = Number(this.input.value) || 0;
    const next = btn.name === 'plus' ? prev + step : prev - step;
    this.input.value = this.clamp(next);
    if (Number(this.input.value) !== prev) {
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
  clamp(v) {
    const min = Number(this.input.min) || 0;
    const max = this.input.max ? Number(this.input.max) : Infinity;
    return Math.min(Math.max(v, min), max);
  }
  sync() {
    const v = Number(this.input.value);
    const min = Number(this.input.min) || 0;
    const max = this.input.max ? Number(this.input.max) : Infinity;
    const minus = this.querySelector('button[name="minus"]');
    const plus = this.querySelector('button[name="plus"]');
    if (minus) minus.disabled = v <= min;
    if (plus) plus.disabled = v >= max;
  }
}
customElements.define('quantity-input', QuantityInput);

class SliderCarousel extends HTMLElement {
  async connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    const Swiper = await ensureSwiper();
    if (!this.isConnected || this.swiper) return;
    if (!Swiper) {

      this.setAttribute('data-no-swiper', '');
      return;
    }
    this.classList.add('swiper');
    this.track.classList.add('swiper-wrapper');
    this.tagSlides();
    this.swiper = new Swiper(this, this.config());
    this.swiper.on('slideChange', () => this.syncStatus());
    this.syncStatus();
    this.toggleAttribute('data-static', this.slides.length < 2);

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', (e) => {
        const slide = this.slides.find((s) => s === e.target || s.contains(e.target));
        if (slide) this.swiper.slideTo(this.slides.indexOf(slide));
      });
    }
  }

  disconnectedCallback() {
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  get slides() {
    return Array.from(this.track.children);
  }

  // Arrows may sit beside the carousel in a .carousel-stage wrapper so they can hang off its sides.
  navButton(selector) {
    return this.querySelector(selector) || this.closest(".carousel-stage")?.querySelector(selector) || null;
  }

  tagSlides() {
    this.slides.forEach((el) => el.classList.add('swiper-slide'));
  }

  refresh() {
    if (!this.swiper) return;
    this.tagSlides();
    this.swiper.update();
    this.toggleAttribute('data-static', this.slides.length < 2);
    this.syncStatus();
  }

  cssNumber(name, fallback, { allowZero = false } = {}) {
    const raw = getComputedStyle(this).getPropertyValue(name).trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) && (allowZero ? n >= 0 : n > 0) ? n : fallback;
  }

  config() {
    const reduce = prefersReducedMotion();
    const perView = this.cssNumber('--per-view', 1);
    const perViewSm = this.cssNumber('--per-view-sm', perView);
    const gap = this.cssNumber('--col-gap', 0);
    const gapSm = this.cssNumber('--col-gap-sm', gap, { allowZero: true });
    const autoplaySpeed = Number(this.dataset.autoplay);
    const loop = this.hasAttribute('loop') && this.slides.length > 1;

    return {
      slidesPerView: perViewSm,
      spaceBetween: gapSm,

      breakpoints: {
        750: { slidesPerView: Math.min(perView, 3), spaceBetween: gap },
        990: { slidesPerView: perView, spaceBetween: gap },
      },
      loop,
      centeredSlides: this.hasAttribute('centered'),
      speed: reduce ? 0 : 400,
      grabCursor: true,
      simulateTouch: true,
      threshold: 4,
      watchSlidesProgress: true,
      navigation: {
        prevEl: this.navButton('[data-prev]'),
        nextEl: this.navButton('[data-next]'),
        disabledClass: 'is-disabled',
      },
      pagination: this.querySelector('[data-dots]')
        ? {
            el: this.querySelector('[data-dots]'),
            clickable: true,
            bulletClass: 'carousel__dot',
            bulletActiveClass: 'is-active',
          }
        : false,
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: {
        enabled: true,
        slideRole: /^(UL|OL)$/.test(this.track.tagName) ? 'listitem' : 'group',
        prevSlideMessage: config.strings?.previous || 'Previous slide',
        nextSlideMessage: config.strings?.next || 'Next slide',
      },
      autoplay:
        autoplaySpeed && !reduce
          ? { delay: autoplaySpeed * 1000, pauseOnMouseEnter: true, disableOnInteraction: false }
          : false,
    };
  }

  syncStatus() {
    const status = this.querySelector('[data-status]');
    if (status && this.swiper) {
      status.textContent = `${this.swiper.realIndex + 1} / ${this.slides.length}`;
    }
  }
}
customElements.define('slider-carousel', SliderCarousel);

/*
  Quick view for products with several options (Size, Color…): one picker per option.
  The chosen values select the matching hidden variant radio (name="id"), which is what the form
  submits and what fizz.css follows to show the right picture, price and buy-now link.
*/
class QuickViewOptions extends HTMLElement {
  connectedCallback() {
    this.dialog = this.closest('.quick-view') || this.parentElement;
    this.variants = Array.from(this.dialog.querySelectorAll('.variant-option__values > input[name="id"]'));
    this.addButton = this.dialog.querySelector('.quick-view__add');
    this.addEventListener('change', (event) => {
      if (event.target.matches('.qv-option__values input')) this.update();
    });
    this.refreshAvailability();
  }

  get groups() {
    return Array.from(this.querySelectorAll('.qv-option'));
  }

  selectedValues() {
    return this.groups.map((group) => group.querySelector('input:checked')?.value ?? '');
  }

  findVariant(values) {
    const key = values.join('|');
    return this.variants.find((input) => input.dataset.options === key);
  }

  update() {
    const values = this.selectedValues();
    const match = this.findVariant(values);
    if (match) {
      match.checked = true;
      match.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const buyable = Boolean(match && !match.disabled);
    if (this.addButton) this.addButton.disabled = !buyable;
    this.dialog.toggleAttribute('data-unavailable', !buyable);
    this.groups.forEach((group, i) => {
      const label = group.querySelector('[data-qv-selected]');
      if (label) label.textContent = values[i];
    });
    this.refreshAvailability();
  }

  // Cross out values that would lead to a sold-out or non-existent variant with the other current picks.
  refreshAvailability() {
    const values = this.selectedValues();
    this.groups.forEach((group, i) => {
      group.querySelectorAll('input').forEach((input) => {
        const combo = values.slice();
        combo[i] = input.value;
        const variant = this.findVariant(combo);
        input.toggleAttribute('data-unavailable', !variant || variant.disabled);
      });
    });
  }
}
customElements.define('quick-view-options', QuickViewOptions);

class ImageCompare extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input[type="range"]');
    const set = () => this.style.setProperty('--pos', `${input.value}%`);
    input?.addEventListener('input', set);
    if (input) set();
  }
}
customElements.define('image-compare', ImageCompare);

class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.addEventListener('focusin', () => {
      if (this.skipOpen) this.skipOpen = false;
      else this.open();
    });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.close();
    });
    this.input?.addEventListener('input', () => this.open());
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.hasAttribute('data-open')) return;
      e.stopPropagation();
      this.close();
      if (document.activeElement !== this.input) {
        this.skipOpen = true;
        this.input?.focus();
      }
    });
    document.addEventListener('pointerdown', this.onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onDocClick);
  }

  onDocClick = (e) => {
    if (!this.contains(e.target)) this.close();
  };

  open() {
    this.setAttribute('data-open', '');
    this.input?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.removeAttribute('data-open');
    this.input?.setAttribute('aria-expanded', 'false');
  }
}
customElements.define('header-search', HeaderSearch);

const swatchPeek = (e) => {
  const swatch = e.target.closest?.('.swatch[data-swatch-image]');
  if (!swatch) return;
  const card = swatch.closest('.card');
  const img = card?.querySelector('.media--primary');
  if (!img) return;

  if (img.dataset.cardSrc === undefined) {
    img.dataset.cardSrc = img.getAttribute('src') || '';
    img.dataset.cardSet = img.getAttribute('srcset') || '';
  }

  img.setAttribute('srcset', '');
  img.setAttribute('src', swatch.dataset.swatchImage);
  card.classList.add('is-swatch');
  const link = card.querySelector('[data-card-image]');
  if (link) {

    if (img.dataset.cardHref === undefined) img.dataset.cardHref = link.getAttribute('href') || '';
    const href = swatch.getAttribute('href');
    if (href) link.setAttribute('href', href);
  }
};

const swatchReset = (e) => {
  const card = e.target.closest?.('.card');
  if (!card || card.contains(e.relatedTarget)) return;
  card.classList.remove('is-swatch');
  const img = card.querySelector('.media--primary');
  if (!img || img.dataset.cardSrc === undefined) return;
  img.setAttribute('srcset', img.dataset.cardSet);
  img.setAttribute('src', img.dataset.cardSrc);
  const link = card.querySelector('[data-card-image]');
  if (link && img.dataset.cardHref !== undefined) link.setAttribute('href', img.dataset.cardHref);
};

document.addEventListener('pointerover', swatchPeek);
document.addEventListener('focusin', swatchPeek);
document.addEventListener('pointerout', swatchReset);
document.addEventListener('focusout', swatchReset);

const hydrateHoverImage = (e) => {

  if (e.pointerType && e.pointerType !== 'mouse') return;
  if (!window.matchMedia('(hover: hover)').matches) return;
  const card = e.target.closest?.('.card');
  const parked = card?.querySelector('template[data-hover-image]');
  if (!parked) return;
  const frame = parked.content.cloneNode(true);
  const video = frame.querySelector('video');
  if (video) {
    video.muted = true;
    parked.replaceWith(frame);
    if (prefersReducedMotion()) return;
    const play = () => video.play().catch(() => {});
    card.addEventListener('pointerenter', play);
    card.addEventListener('pointerleave', () => video.pause());
    play();
    return;
  }
  const img = frame.querySelector('img');
  if (img) img.loading = 'eager';

  parked.replaceWith(frame);
  if (!img) return;

  img.style.opacity = '0';
  const reveal = () => requestAnimationFrame(() => (img.style.opacity = ''));
  if (img.complete) reveal();
  else img.addEventListener('load', reveal, { once: true });
};

document.addEventListener('pointerover', hydrateHoverImage);

const scrollTopButton = document.querySelector('[data-scroll-top]');
if (scrollTopButton) {
  const syncScrollTop = () => {
    scrollTopButton.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.75);
  };
  syncScrollTop();
  window.addEventListener('scroll', syncScrollTop, { passive: true });
  scrollTopButton.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });

    document.getElementById('MainContent')?.focus({ preventScroll: true });
  });
}

class AnnouncementRotator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mode === 'marquee') {
      setupMarquee(this);
      return;
    }
    this.slides = Array.from(this.querySelectorAll('.announcement__slide'));
    if (this.slides.length < 2) return;
    this.i = 0;
    this.show(0);
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.i - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.i + 1));
    const speed = Number(this.dataset.speed) || 5;
    if (!prefersReducedMotion() && speed) {
      const run = () => (this.timer = setInterval(() => this.show(this.i + 1), speed * 1000));
      run();
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', run);
      this.addEventListener('focusin', () => clearInterval(this.timer));
    }
    this.addEventListener('shopify:block:select', (e) => {
      clearInterval(this.timer);
      this.show(this.slides.indexOf(e.target));
    });
  }
  disconnectedCallback() {
    clearInterval(this.timer);
  }
  show(n) {
    this.i = (n + this.slides.length) % this.slides.length;
    this.slides.forEach((s, idx) => {
      s.setAttribute('aria-hidden', String(idx !== this.i));
      s.toggleAttribute('inert', idx !== this.i);
    });
  }
}
customElements.define('announcement-rotator', AnnouncementRotator);

function setupMarquee(root) {
  const track = root.querySelector('.marquee__track');
  if (!track || root.dataset.ready) return;
  root.dataset.ready = 'true';
  const original = Array.from(track.children);
  if (!original.length) return;
  const hide = (el) => {
    el.setAttribute('aria-hidden', 'true');
    el.removeAttribute('data-shopify-editor-block');
    el.querySelectorAll('a, button, input').forEach((n) => n.setAttribute('tabindex', '-1'));
    el.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
    return el;
  };
  const needed = root.clientWidth * 1.2;
  const unit = track.scrollWidth;
  let width = unit;
  let guard = 0;
  while (unit > 0 && width < needed && guard < 8) {
    original.forEach((el) => track.append(hide(el.cloneNode(true))));
    width += unit;
    guard += 1;
  }
  root.append(hide(track.cloneNode(true)));
}

class MarqueeScroller extends HTMLElement {
  connectedCallback() {
    setupMarquee(this);
  }
}
customElements.define('marquee-scroller', MarqueeScroller);

class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-value]');
      if (!btn) return;
      e.preventDefault();
      const form = this.querySelector('form');
      const input = form.querySelector(`input[name="${btn.dataset.name}"]`);
      input.value = btn.dataset.value;
      form.submit();
    });
  }
}
customElements.define('localization-form', LocalizationForm);

class AutoPopup extends HTMLElement {
  connectedCallback() {
    const dialog = this.parentElement?.querySelector('dialog');
    const key = `Diecela-popup-${this.dataset.id}`;
    if (!dialog || window.Shopify?.designMode) return;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch (e) {  }
    setTimeout(() => {
      if (!document.querySelector('dialog[open]')) openDialog(dialog);
      try { sessionStorage.setItem(key, '1'); } catch (e) {  }
    }, (Number(this.dataset.delay) || 5) * 1000);
  }
}
customElements.define('auto-popup', AutoPopup);

class ShareButton extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', async () => {
      const url = this.dataset.url || window.location.href;
      const title = this.dataset.title || document.title;
      if (navigator.share) {
        try { await navigator.share({ url, title }); } catch (e) {  }
        return;
      }
      await navigator.clipboard?.writeText(url);
      announce(config.strings?.linkCopied || 'Link copied', { toast: true });
    });
  }
}
customElements.define('share-button', ShareButton);

function collapseOnMobile(root = document) {
  if (!window.matchMedia('(max-width: 749px)').matches) return;
  root.querySelectorAll('details[data-mobile-collapse][open]').forEach((d) => d.removeAttribute('open'));
}
collapseOnMobile();
document.addEventListener('shopify:section:load', (e) => collapseOnMobile(e.target));

function swipeAutoplay(root = document) {
  if (prefersReducedMotion()) return;
  const small = window.matchMedia('(max-width: 749px)');

  root.querySelectorAll('[data-swipe-autoplay]').forEach((row) => {
    const seconds = Number(row.dataset.swipeAutoplay);
    if (!seconds || row.dataset.swipePlaying) return;
    row.dataset.swipePlaying = '1';

    let timer = null;
    let onScreen = false;

    const stop = () => {
      clearInterval(timer);
      timer = null;
    };

    const advance = () => {
      const first = row.firstElementChild;
      if (!first) return;
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      const step = first.getBoundingClientRect().width + gap;

      const end = row.scrollWidth - row.clientWidth - 2;
      row.scrollTo({ left: row.scrollLeft >= end ? 0 : row.scrollLeft + step, behavior: 'smooth' });
    };

    const start = () => {
      if (!timer && onScreen && small.matches) timer = setInterval(advance, seconds * 1000);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        onScreen ? start() : stop();
      },
      { threshold: 0.4 }
    );
    io.observe(row);
    small.addEventListener('change', () => (small.matches ? start() : stop()));

    row.addEventListener(
      'pointerdown',
      () => {
        stop();
        io.disconnect();
      },
      { once: true }
    );
  });
}
swipeAutoplay();
document.addEventListener('shopify:section:load', (e) => swipeAutoplay(e.target));

document.querySelectorAll('dialog[data-open-on-load]').forEach((d) => openDialog(d));

// drop the media placeholder (background + shimmer) once its image/video has loaded
{
  const markLoaded = (el) => el.closest('.media')?.classList.add('is-loaded');
  const scan = () =>
    document.querySelectorAll('.media:not(.is-loaded) img, .media:not(.is-loaded) video').forEach((el) => {
      if ((el.tagName === 'IMG' && el.complete && el.naturalWidth) || (el.tagName === 'VIDEO' && el.readyState >= 2)) markLoaded(el);
    });
  document.addEventListener('load', (e) => e.target.tagName === 'IMG' && markLoaded(e.target), true);
  document.addEventListener('loadeddata', (e) => e.target.tagName === 'VIDEO' && markLoaded(e.target), true);
  scan();
  document.addEventListener('DOMContentLoaded', scan);
  document.addEventListener('shopify:section:load', scan);
}
